import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    FaBan,
    FaCheck,
    FaCheckCircle,
    FaExclamation,
    FaPlay,
    FaPlayCircle,
    FaPoll
} from 'react-icons/fa';
import {
    MdExpandLess,
    MdExpandMore,
    MdOndemandVideo,
    MdOutlineArrowBack,
    MdOutlineQuiz
} from 'react-icons/md';
import { RiFlagFill, RiMoreFill } from 'react-icons/ri';
import { SiDiscord, SiNotion } from 'react-icons/si';
import Player from '@vimeo/player';
import moment from 'moment';
import Helmet from 'components/Helmet';
import axios from 'config/axios';
import { useAppDispatch } from 'hooks/useStore';
import { setAlert, setError } from 'store/error';
import './index.css';

function Course() {
    const [course, setCourse] = useState(null);
    const [currentWeek, setCurrentWeek] = useState(null);
    const [currentLecture, setCurrentLecture] = useState(null);
    const [lastWeek, setLastWeek] = useState(null);
    const [lastLecture, setLastLecture] = useState(null);
    const [lectureGroupOpen, setLectureGroupOpen] = useState({});
    const [progress, setProgress] = useState(0);
    const [player, setPlayer] = useState(null);
    const [showNext, setShowNext] = useState(false);
    const [timerActive, setTimerActive] = useState(false);
    const [playTime, setPlayTime] = useState(-1);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { courseUrl } = useParams();
    const [scrollPosition, setScrollPosition] = useState(0);
    const [isMobile, setIsMobile] = useState(false);
    const currentLectureGroupRef = useRef(null);
    const courseRef = useRef(null);
    const [maxScrollHeight, setMaxScrollHeight] = useState(0);

    useEffect(() => {
        if (courseRef.current) {
            const handleResize = () => {
                const rect = courseRef.current.getBoundingClientRect();
                const screenHeight = window.innerHeight;
                setMaxScrollHeight(rect.height - screenHeight);
            };

            handleResize();

            window.addEventListener('resize', handleResize);
            return () => {
                window.removeEventListener('resize', handleResize);
            };
        }
    }, [course, lectureGroupOpen]);

    // scroll event
    const handleScroll = () => {
        // setScrollPosition(window.scrollY);     
        if (window.scrollY < maxScrollHeight) {
            setScrollPosition(window.scrollY);        
        }
    };

    const checkIsMobile = () => {
        setIsMobile(window.innerWidth <= 1024); 
    };

    // go to current lecturegroup when the page is loaded
    useEffect(() => {
        if (!isMobile && currentLectureGroupRef.current) {
            currentLectureGroupRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [currentWeek, isMobile]);
    
    useEffect(() => {
        checkIsMobile();
        window.addEventListener('resize', checkIsMobile);
        return () => {
            window.removeEventListener('resize', checkIsMobile);
        };
    }, []);

    useEffect(() => {
        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [maxScrollHeight]);
      


    const toggleLectureGroupOpen = (groupNumber: number) => {
        setLectureGroupOpen({
            ...lectureGroupOpen,
            [groupNumber]: !lectureGroupOpen[groupNumber]
        });
    }

    const goToLecture = (week: number, lecture: number) => {
        if (timerActive || playTime > -1) {
            axios.post(`courses/classroom/lecture/${course.lectureGroups[currentWeek].lectures[currentLecture].id}/`, {
                    viewTime: playTime
                })
                .then(res => {
                    let newCourse = { ...course };
                    newCourse.lectureGroups[currentWeek].lectures[currentLecture].watched = true;
                    setCourse(newCourse);
                    if (lastWeek === week && lastLecture < lecture) {
                        setLastLecture(newCourse.lectureGroups[lastWeek].lectures.findIndex((lecture, i) => (
                            i === newCourse.lectureGroups[lastWeek].lectures.length - 1
                                ? true
                                : !lecture.watched && lecture.recommended === 'Critical'
                        )));
                    }
                    setProgress(parseFloat((res.data.progress * 100).toFixed(1)));
                    setTimerActive(false);
                    setPlayTime(-1);
                })
                .catch(error => {
                    // console.log(error);
                    dispatch(setAlert(error));
                });
        }
        setShowNext(false);
        setCurrentWeek(week);
        setCurrentLecture(lecture);
    }

    const goToQuiz = (week: number) => {
        if (timerActive || playTime > -1) {
            axios.post(`courses/classroom/lecture/${course.lectureGroups[currentWeek].lectures[currentLecture].id}/`, {
                viewTime: playTime
            })
                .then(() => {
                    navigate(`quiz/${week}`);
                })
                .catch(error => {
                    // console.log(error);
                    dispatch(setError(error));
                });
        }
        else {
            navigate(`quiz/${week}`);
        }
    }

    const goToPostTest = () => {
        if (timerActive || playTime > -1) {
            axios.post(`courses/classroom/lecture/${course.lectureGroups[currentWeek].lectures[currentLecture].id}/`, {
                viewTime: playTime
            })
                .then(() => {
                    navigate(`test/${course.postTest.testId}`);
                })
                .catch(error => {
                    // console.log(error);
                    dispatch(setError(error));
                });
        }
        else {
            navigate(`test/${course.postTest.testId}`);
        }
    }

    useEffect(() => {
        axios.get(`courses/classroom/${courseUrl}/`)
            .then(res => {
                const { course } = res.data;
                setCourse(course);

                const { lectureGroups } = course;

                const lectureGroupIndex = Math.min(lectureGroups.filter(lectureGroup => lectureGroup.finished).length, lectureGroups.length - 1);
                setCurrentWeek(lectureGroupIndex);
                setLastWeek(lectureGroupIndex);

                const { lectures } = lectureGroups[lectureGroupIndex];

                const lastLectureIndex = lectures.findIndex((lecture, i) => (i === lectures.length - 1
                    ? true
                    : !lecture.watched && lecture.recommended === 'Critical'
                ));
                setLastLecture(lastLectureIndex);

                const currentLectureIndex = Math.min(lectures.findLastIndex(lecture => lecture.watched) + 1, lectures.length - 1);
                setCurrentLecture(currentLectureIndex);

                setLectureGroupOpen(lectureGroups.reduce((open, lectureGroup) => ({
                    ...open, 
                    [lectureGroup.groupNumber]: lectureGroup.groupNumber === lectureGroupIndex + 1
                }), {}));

                setProgress(parseFloat((course.progress * 100).toFixed(1)));
            })
            .catch(error => {
                // console.log(error);
                dispatch(setError(error));
            });
    }, []);

    useEffect(()=>{
        if (progress==100){
            setLectureGroupOpen({});
        }
    }, [progress])

    useEffect(() => {
        if (course) {
            if (player) {
                player.loadVideo(course.lectureGroups[currentWeek].lectures[currentLecture].videoUrl);
            }
            else {
                setPlayer(new Player('vimeoPlayer', {
                    url: course.lectureGroups[currentWeek].lectures[currentLecture].videoUrl
                }));
            }
        }
    }, [currentLecture, currentWeek]);

    useEffect(() => {
        if (player) {
            player.on('playing', () => {
                // console.log('playing');
                setTimerActive(true);
            });
            player.on('pause', () => {
                // console.log('pause');
                setTimerActive(false);
            });
            player.on('ended', () => {
                // console.log('ended');
                setShowNext(true);
                setTimerActive(false);
            });
        }
    }, [player]);

    useEffect(() => {
        let timer = null;
        if (timerActive) {
            if (playTime < 0) setPlayTime(0);
            timer = setInterval(() => {
                setPlayTime(playTime + 1);
            }, 1000);
        }
        else {
            clearInterval(timer);
        }
        // console.log(playTime);
        return () => clearInterval(timer);
    }, [timerActive, playTime]);

    if (course === null) return <></>;

    return (
        <div className='layout'>
            <Helmet
                title={`${course.name} - The Digital SAT Crash Course`}
            />

            <button
                className='course__back'
                onClick={() => navigate('/courses')}
            >
                <MdOutlineArrowBack className='course__backIcon' />
                Back to My Courses
            </button>
            <div className='course' ref={courseRef}>
                <div 
                    className='course__mainColumn' 
                    style={!isMobile ? { transform: `translateY(${scrollPosition}px)`, transition: 'transform 0.5s cubic-bezier(0.25, 0.1, 0.25, 1)' } : {}}
                >
                    <div className='course__lectureVideoContainer'>
                        <div id='vimeoPlayer'></div>
                        {showNext && (
                            currentLecture < course.lectureGroups[currentWeek].lectures.length - 1 ||
                            course.lectureGroups[currentWeek].quiz.id
                        ) &&
                            <button
                                className='course__lectureVideoNext' 
                                onClick={() => currentLecture === course.lectureGroups[currentWeek].lectures.length - 1
                                    ? (course.lectureGroups[currentWeek].quiz.id
                                        ? goToQuiz(course.lectureGroups[currentWeek].groupNumber)
                                        : goToPostTest()
                                      )
                                    : goToLecture(currentWeek, currentLecture + 1)
                                }
                            >
                                <div className='course__lectureVideoNextInfo'>
                                    <p>Next up</p>
                                    <h4 className='course__lectureVideoNextTitle'>
                                        {currentLecture === course.lectureGroups[currentWeek].lectures.length - 1
                                            ? (course.lectureGroups[currentWeek].quiz.id
                                                ? `${course.lectureGroups[currentWeek].name} Quiz`
                                                : `${course.name} Post Test` 
                                              )
                                            : course.lectureGroups[currentWeek].lectures[currentLecture + 1].name
                                        }
                                    </h4>
                                    {currentLecture === course.lectureGroups[currentWeek].lectures.length - 1
                                        ? <FaPoll className='course__lectureVideoNextIcon' />
                                        : <FaPlayCircle className='course__lectureVideoNextIcon' />
                                    }
                                    {/* <FaPlayCircle className='course__lectureVideoNextIcon' /> */}
                                </div>
                            </button>
                        }
                    </div>
                    <div className='course__courseInfo'>
                        <div className='course__courseInfoTabs'>
                            <button className='course__courseInfoTab--active'>
                                Overview
                            </button>
                        </div>
                        <table className='course__courseInfoTable'>
                            <tbody>
                                <tr>
                                    <th>Course Name</th>
                                    <td>{course.name}</td>
                                </tr>
                                <tr>
                                    <th>Course Period</th>
                                    <td>{moment(course.startDate).format('ll')} - {moment(course.endDate).format('ll')}</td>
                                </tr>
                                {course.teacher &&
                                    <tr>
                                        <th>Teacher</th>
                                        <td>{course.teacher.firstName} {course.teacher.lastName}</td>
                                    </tr>
                                }
                                <tr>
                                    <th>Lectures</th>
                                    <td>
                                        {course.lectureGroups.reduce(
                                            (numLectures, lectureGroup) => numLectures + lectureGroup.lectures.length,
                                            0
                                        )} lectures, {Math.floor(course.lectureGroups.reduce(
                                            (duration, lectureGroup) => duration.add(lectureGroup.duration),
                                            moment.duration(0)).hours()
                                        )} hours {Math.floor(course.lectureGroups.reduce(
                                            (duration, lectureGroup) => duration.add(lectureGroup.duration),
                                            moment.duration(0)).minutes()
                                        )} minutes
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
                <div className='course__lectureColumn'>
                    <div className='course__lectureProgress'>
                        <h3 className='course__lectureProgressText'>
                            Critical Lectures and Quizzes
                            <span>{progress}% complete</span>
                        </h3>
                        <div className='course__lectureProgressChart'>
                            <div style={{ width: `${progress}%` }}></div>
                        </div>
                    </div>
                    {course.lectureGroups.map((lectureGroup, lectureGroupIndex) => (
                        <React.Fragment key={lectureGroup.name}>
                            <div 
                                ref={lectureGroupIndex === currentWeek ? currentLectureGroupRef : null}
                                className={lectureGroupIndex <= lastWeek
                                    ? 'course__lectureGroup' 
                                    : 'course__lectureGroup--disabled'
                                }
                                onClick={() => {
                                    if (lectureGroupIndex <= lastWeek) toggleLectureGroupOpen(lectureGroup.groupNumber);
                                }}
                            >
                                <div className='course__lectureGroupTitle'>
                                    {/* <p className='course__lectureGroupWeek'>week {lectureGroup.groupNumber}</p> */}
                                    <h4 className='course__lectureGroupName'>{lectureGroup.groupNumber}. {lectureGroup.name}</h4>
                                </div>
                                <div className='course__lectureGroupInfo'>
                                    <p className='course__lectureGroupDuration'>
                                        <MdOndemandVideo className='course__lectureGroupDurationIcon' />
                                        {Math.floor(moment.duration(lectureGroup.duration).asMinutes())} min
                                    </p>
                                    {lectureGroupIndex < lastWeek || lastWeek === course.lectureGroups.length - 1
                                        ?   <p className='course__lectureGroupStatus'>
                                                <FaCheckCircle className='course__lectureGroupStatusIcon' />
                                                completed
                                            </p>
                                        :   lectureGroupIndex == lastWeek
                                        ?   <p className='course__lectureGroupStatus'>
                                                <FaPlayCircle className='course__lectureGroupStatusIcon' />
                                                studying
                                            </p>
                                        :   <p className='course__lectureGroupStatus'>
                                                <FaBan className='course__lectureGroupStatusIcon' />
                                                not available
                                            </p>
                                    }
                                </div>
                                {lectureGroupOpen[lectureGroup.groupNumber]
                                    ? <MdExpandLess className='course__lectureGroupExpandIcon' />
                                    : <MdExpandMore className='course__lectureGroupExpandIcon' />
                                }
                            </div>
                            {lectureGroupIndex <= lastWeek && lectureGroupOpen[lectureGroup.groupNumber] &&
                                <div className='course__lectureList'>
                                    <a
                                        className='course__lectureGroupNotes'
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        href={lectureGroup.syllabusUrl}
                                    >
                                        {/* <SiNotion className='course__lectureGroupNotesIcon' />
                                        Week {lectureGroup.groupNumber} Notes */}
                                    </a>
                                    {lectureGroup.lectures.map((lecture, lectureIndex) => (
                                        <div
                                            className={lectureGroupIndex === currentWeek && lectureIndex === currentLecture
                                                ? 'course__lecture--current'
                                                : lectureGroupIndex < lastWeek || lectureIndex <= lastLecture
                                                ? 'course__lecture'
                                                : 'course__lecture--disabled'
                                            }
                                            key={lecture.name}
                                            onClick={() => (lectureGroupIndex < lastWeek || lectureIndex <= lastLecture) && goToLecture(lectureGroupIndex, lectureIndex)}
                                        >
                                            {lecture.recommended === 'Satisfactory'
                                                ? <RiFlagFill className='course__lectureSatisfactoryIcon' />
                                                : lecture.recommended === 'Advised to Review'
                                                ? <RiMoreFill className='course__lectureAdvisedIcon' />
                                                : lecture.recommended === 'Critical'
                                                ? <FaExclamation className='course__lectureCriticalIcon' />
                                                : <RiMoreFill className='course__lectureAdvisedIcon' />
                                            }
                                            <small className='course__lectureRecommendation'>
                                                {lecture.recommended ? lecture.recommended : 'Advised to Review'}
                                            </small>
                                            {lectureGroupIndex === currentWeek && lectureIndex === currentLecture
                                                ? <FaPlay className='course__lectureStatusIcon' />
                                                : lecture.watched
                                                ? <FaCheck className='course__lectureStatusIcon' />
                                                : <></>
                                            }
                                            <p className='course__lectureName'>{lecture.name}</p>
                                            <p className='course__lectureDuration'>
                                                <MdOndemandVideo className='course__lectureDurationIcon' />
                                                {Math.round(moment.duration(lecture.duration).asMinutes())} min
                                            </p>
                                        </div>
                                    ))}
                                    {course.lectureGroups[lectureGroupIndex].quiz.id &&
                                        <div
                                            className={course.lectureGroups[lectureGroupIndex].lectures.filter(lecture => (
                                                    lecture.recommended === 'Critical' && !lecture.watched
                                                )).length === 0
                                                    ? 'course__lecture'
                                                    : 'course__lecture--disabled'
                                            }
                                            onClick={()=>(lectureGroupIndex < lastWeek || progress==100? 
                                                navigate(`quiz/${lectureGroupIndex+1}/result`)
                                                :(course.lectureGroups[lectureGroupIndex].lectures.filter(lecture => (
                                                    lecture.recommended === 'Critical' && !lecture.watched
                                                )).length === 0) ? goToQuiz(course.lectureGroups[lectureGroupIndex].groupNumber):null
                                            )}                                         
                                        >
                                            <FaExclamation className='course__lectureCriticalIcon' />
                                            <small className='course__lectureRecommendation'>Critical</small>
                                            {(lectureGroupIndex < lastWeek || (lastWeek === course.lectureGroups.length - 1)&&course.lectureGroups[lectureGroupIndex].quiz.passed) && <FaCheck className='course__lectureStatusIcon' />}
                                            <p className='course__lectureName'>{lectureGroup.name} Quiz</p>
                                            <p className='course__lectureDuration'>
                                                <MdOutlineQuiz className='course__lectureDurationIcon' />
                                                {lectureGroup.quiz.numCorrect
                                                    ? `${lectureGroup.quiz.numCorrect}/${lectureGroup.quiz.numQuestions}`
                                                    : `${lectureGroup.quiz.numQuestions} Questions`
                                                }
                                            </p>
                                        </div>
                                    }
                                </div>
                            }
                        </React.Fragment>
                    ))}
                    <div
                        className={progress==100
                            ? 'course__lectureGroup' 
                            : 'course__lectureGroup--disabled'
                        }
                        onClick={()=> {if(progress==100){
                            course.isPostTestTaken?
                            navigate(`/courses/report/${course.postTestId}`):
                            navigate(`/test/${course.postTestId}`)
                        }}}
                    >
                        {course.isPostTestTaken && <FaCheck className='course__lectureStatusIcon' />}
                        <p className='course__lectureName'>Post Test</p>
                        {course.isPostTestTaken&&
                            <p className='course__lectureDuration'>
                                <MdOutlineQuiz className='course__lectureDurationIcon' />
                                {course.isPostTestTaken
                                    ? `Score: ${course.PostTestScore}`
                                    : null
                                }
                            </p>
                        }
                    </div>
            </div>
            </div>
        </div>
    );
}

export default Course;